import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarDays, CheckCircle2, ChevronRight, CircleX, Clock3, ListChecks,
  MessageSquareText, Server, Workflow, X,
} from 'lucide-react';

import {
  loadPipelineDeployment, loadPipelineRelease, loadPipelineReleases,
} from './api';
import { PipelineDetail } from './XomicsPipelines';
import {
  Empty, Failed, Head, Loading, Pill, SearchField, SummaryBar, SummaryItem,
  ViewSwitch, XomicsSelect,
} from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import { statusLabel } from './labels';

function when(value) {
  if (!value) return '—';
  return new Date(Number(value)).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function compactId(value, length = 12) {
  const text = String(value ?? '');
  return text.length > length ? text.slice(0, length) : text;
}

function span(value) {
  const milliseconds = Number(value || 0);
  if (milliseconds < 1000) return `${milliseconds} ms`;
  if (milliseconds < 60000) return `${(milliseconds / 1000).toFixed(1)} s`;
  return `${Math.floor(milliseconds / 60000)}m ${Math.round((milliseconds % 60000) / 1000)}s`;
}

function attemptTone(status) {
  if (status === 'active') return 'ok';
  if (status === 'failed') return 'bad';
  return 'warn';
}

function ReleaseRow({ release, attempt, onOpen }) {
  return (
    <button type="button" className={`xo-release-row${release.status === 'active' ? ' is-active' : ''}`} onClick={onOpen}>
      <span className="xo-lines">
        <span className="xo-release-title"><strong>{release.tag}</strong><Pill tone={release.status === 'active' ? 'ok' : 'flat'}>{statusLabel(release.status)}</Pill></span>
        <span className="xo-release-commit">{compactId(release.gitCommit)}</span>
        <span className="xo-release-note">
          <MessageSquareText aria-hidden="true" />
          <small className="xo-release-note-text">{release.notes || tx("ui.releases.no_release_notes_supplied_4189e0cb")}</small>
        </span>
      </span>
      <span className="xo-release-details">
        <span className="xo-release-detail"><Workflow aria-hidden="true" /><span>
          <strong>{release.pipelineCount}</strong><small>{tx("ui.releases.pipelines_c5df1e14")}</small>
        </span></span>
        <span className="xo-release-detail"><Server aria-hidden="true" /><span>
          <strong>{release.nodeCount}</strong><small>{tx("ui.releases.nodes_9e325452")}</small>
        </span></span>
        <span className="xo-release-detail"><ListChecks aria-hidden="true" /><span>
          <strong>{attempt?.check_count ?? '—'}</strong><small>{tx("ui.releases.checks_5098dd19")}</small>
        </span></span>
        <span className="xo-release-detail is-date"><CalendarDays aria-hidden="true" /><span>
          <strong>{when(release.createdAt)}</strong><small>{tx("ui.releases.created_accf40c8")}</small>
        </span></span>
      </span>
      <ChevronRight aria-hidden="true" />
    </button>
  );
}

function AttemptRow({ attempt, onOpen }) {
  const runtime = attempt.finished_at && attempt.started_at
    ? Number(attempt.finished_at) - Number(attempt.started_at) : 0;
  return (
    <button type="button" className="xo-deployment-row" onClick={onOpen}>
      <span className={`xo-deployment-icon t-${attemptTone(attempt.status)}`} aria-hidden="true">
        {attempt.status === 'active' ? <CheckCircle2 /> : attempt.status === 'failed' ? <CircleX /> : <Clock3 />}
      </span>
      <span className="xo-lines">
        <strong>{tx("ui.releases.attempt_value_value2_784dd075", { value: compactId(attempt.attempt_id, 10), value2: compactId(attempt.git_commit) })}</strong>
        <span>{attempt.summary || tx("ui.releases.deployment_still_running_12ef296d")}</span>
      </span>
      <span className="xo-deployment-counts">
        <span className="t-ok">{tx("ui.releases.passed_count_passed_1581985f", { passed_count: attempt.passed_count })}</span>
        {Number(attempt.warning_count) > 0 && <span className="t-warn">{tx("ui.releases.warning_count_warnings_c5bf76d0", { warning_count: attempt.warning_count })}</span>}
        {Number(attempt.failed_count) > 0 && <span className="t-bad">{tx("ui.releases.failed_count_failed_e297efd9", { failed_count: attempt.failed_count })}</span>}
      </span>
      <span><Pill tone={attemptTone(attempt.status)}>{statusLabel(attempt.status)}</Pill><small>{span(runtime)}</small></span>
      <ChevronRight aria-hidden="true" />
    </button>
  );
}

function Modal({ labelledBy, onClose, children, wide = false }) {
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
  return (
    <div className="xo-pipeline-modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className={`xo-release-modal${wide ? ' is-wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={labelledBy}>{children}</section>
    </div>
  );
}

function DeploymentDetail({ id, onClose }) {
  const load = useCallback(() => loadPipelineDeployment(id), [id]);
  const resource = useLiveResource(['pipelines'], load);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const deployment = resource.data;
  const checks = useMemo(() => {
    if (!deployment) return [];
    const needle = query.trim().toLowerCase();
    return deployment.checks.filter((check) => {
      if (status !== 'all' && check.status !== status) return false;
      if (needle === '') return true;
      return [check.check_code, check.pipeline_name, check.pipeline_mode, check.target, check.message]
        .some((value) => value && String(value).toLowerCase().includes(needle));
    });
  }, [deployment, query, status]);

  return (
    <Modal labelledBy="deployment-title" onClose={onClose} wide>
      {resource.loading && deployment === null ? <Loading label={tx("ui.releases.reading_every_deployment_gate_322851ee")} />
        : resource.error && deployment === null ? <Failed message={resource.error} onRetry={resource.refresh} />
          : (
            <>
              <header className="xo-pipeline-modal-head">
                <div><span className="xo-pipeline-modal-flags"><Pill tone={attemptTone(deployment.status)}>{statusLabel(deployment.status)}</Pill></span>
                  <h2 id="deployment-title">{tx("ui.releases.deployment_attempt_value_eaa8cc49", { value: compactId(deployment.attempt_id, 10) })}</h2>
                  <p>{compactId(deployment.git_commit)}</p></div>
                <button type="button" className="xo-btn-icon" aria-label={tx("ui.releases.close_deployment_b1e4cae2")} onClick={onClose}><X aria-hidden="true" /></button>
              </header>
              <div className="xo-release-modal-scroll">
                <div className="xo-deployment-summary">
                  <span><small>{tx("ui.releases.started_faa9e7e7")}</small><strong>{when(deployment.started_at)}</strong></span>
                  <span><small>{tx("ui.releases.finished_355bcc57")}</small><strong>{when(deployment.finished_at)}</strong></span>
                  <span><small>{tx("ui.releases.release_d41f56ce")}</small><strong>{deployment.release_id ? tx("ui.releases.release_id_34c4dc26", { release_id: deployment.release_id }) : tx("ui.releases.not_created_97bbb8ce")}</strong></span>
                  <span><small>{tx("ui.releases.request_4aed03ca")}</small><strong>{tx("ui.releases.value_bytes_cb11fca1", { value: Number(deployment.request_bytes || 0).toLocaleString() })}</strong></span>
                  <span><small>{tx("ui.releases.checks_5098dd19")}</small><strong>{deployment.check_count}</strong></span>
                  <span className="t-ok"><small>{tx("ui.releases.passed_271d60f4")}</small><strong>{deployment.passed_count}</strong></span>
                  <span className={Number(deployment.warning_count) > 0 ? 't-warn' : ''}><small>{tx("ui.releases.warnings_1430f976")}</small><strong>{deployment.warning_count}</strong></span>
                  <span className={Number(deployment.failed_count) > 0 ? 't-bad' : ''}><small>{tx("ui.releases.failed_09fef5d8")}</small><strong>{deployment.failed_count}</strong></span>
                </div>
                <p className={`xo-alert t-${deployment.status === 'failed' ? 'bad' : 'ok'}`}>{deployment.summary}</p>
                <div className="xo-release-toolbar">
                  <SearchField value={query} onChange={setQuery}
                    placeholder={tx("ui.releases.find_gate_pipeline_target_or_message_f20238db")}
                    label={tx("ui.releases.search_deployment_checks_b2333cb1")} />
                  <XomicsSelect value={status} aria-label={tx("ui.releases.check_status_07adf97a")} onChange={(event) => setStatus(event.target.value)}>
                    <option value="all">{tx("ui.releases.all_checks_3109219b")}</option><option value="passed">{tx("ui.releases.passed_271d60f4")}</option>
                    <option value="warning">{tx("ui.releases.warnings_1430f976")}</option><option value="failed">{tx("ui.releases.failed_09fef5d8")}</option>
                  </XomicsSelect>
                </div>
                <div className="xo-deployment-checks">
                  {checks.map((check) => (
                    <article key={check.order_index} className={`t-${check.status}`}>
                      <span className="xo-check-order">{check.order_index}</span>
                      <span className="xo-lines"><strong>{check.check_code}</strong>
                        <span>{check.pipeline_name ? tx("ui.releases.pipeline_namevalue_fa7fe596", { pipeline_name: check.pipeline_name, value: check.pipeline_mode ? `:${check.pipeline_mode}` : '' }) : tx("ui.releases.release_wide_5786ab34")}
                          {check.target ? tx("ui.releases.target_7a3f1ad0", { target: check.target }) : ''}</span>
                        <small>{check.message}</small></span>
                      <Pill tone={check.status === 'passed' ? 'ok' : check.status === 'failed' ? 'bad' : 'warn'}>{statusLabel(check.status)}</Pill>
                      <span className="xo-mono">{span(check.duration_ms)}</span>
                    </article>
                  ))}
                </div>
                <p className="xo-list-foot">{tx("ui.releases.showing_length_of_length2_ordered_checks_0fb0ec8f", { length: checks.length, length2: deployment.checks.length })}</p>
              </div>
            </>
          )}
    </Modal>
  );
}

function ReleaseDetail({ id, onClose, onOpenAttempt, onOpenPipeline }) {
  const load = useCallback(() => loadPipelineRelease(id), [id]);
  const resource = useLiveResource(['pipelines'], load);
  const release = resource.data;
  return (
    <Modal labelledBy="release-title" onClose={onClose} wide>
      {resource.loading && release === null ? <Loading label={tx("ui.releases.reading_release_manifest_836af2f8")} />
        : resource.error && release === null ? <Failed message={resource.error} onRetry={resource.refresh} />
          : (
            <>
              <header className="xo-pipeline-modal-head">
                <div className="xo-release-modal-title">
                  <div><h2 id="release-title">{release.tag}</h2><Pill tone={release.status === 'active' ? 'ok' : 'flat'}>{statusLabel(release.status)}</Pill></div>
                  <p>{compactId(release.gitCommit)}</p>
                </div>
                <button type="button" className="xo-btn-icon" aria-label={tx("ui.releases.close_release_177b6b06")} onClick={onClose}><X aria-hidden="true" /></button>
              </header>
              <div className="xo-release-modal-scroll">
                <p className="xo-release-notes">{release.notes || tx("ui.releases.no_release_notes_supplied_4189e0cb")}</p>
                <div className="xo-deployment-summary">
                  <span><small>{tx("ui.releases.created_accf40c8")}</small><strong>{when(release.createdAt)}</strong></span>
                  <span><small>{tx("ui.releases.updated_f2f8570d")}</small><strong>{when(release.updatedAt)}</strong></span>
                  <span><small>{tx("ui.releases.pipelines_c5df1e14")}</small><strong>{release.pipelineCount}</strong></span>
                  <span><small>{tx("ui.releases.targeted_nodes_cba00b31")}</small><strong>{release.nodeCount}</strong></span>
                </div>

                <section className="xo-release-section"><h3>{tx("ui.releases.pipeline_manifest_4649fbe9")}</h3>
                  <div className="xo-table-wrap"><table className="xo-table"><thead><tr>
                    <th>{tx("ui.releases.pipeline_32b1d5a7")}</th><th>{tx("ui.releases.scope_4651a34e")}</th><th>{tx("ui.releases.image_50e19fda")}</th><th>{tx("ui.releases.inputs_bbcf64a3")}</th><th>{tx("ui.releases.outputs_7835db44")}</th><th>{tx("ui.releases.importable_7a0d33ac")}</th><th>{tx("ui.releases.columns_cf723c59")}</th><th>{tx("ui.releases.references_5d20d0fe")}</th><th />
                  </tr></thead><tbody>{release.pipelines.map((pipeline) => <tr key={pipeline.id}
                    className="xo-click" role="button" tabIndex={0}
                    aria-label={tx("ui.releases.open_displayname_contract_49d05518", { displayName: pipeline.displayName })}
                    onClick={() => onOpenPipeline(pipeline.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onOpenPipeline(pipeline.id);
                      }
                    }}>
                    <td><strong>{pipeline.displayName}</strong><small className="xo-cell-note">{pipeline.key} · v{pipeline.codeVersion}</small></td>
                    <td><Pill tone={pipeline.isPatient ? 'info' : 'flat'}>{pipeline.isPatient ? tx("ui.releases.patient_de5c57ff") : tx("ui.releases.cohort_external_8d852d23")}</Pill><small className="xo-cell-note">{pipeline.omics}</small></td>
                    <td className="xo-mono">{pipeline.imageTag}</td>
                    <td className="xo-num">{pipeline.inputs.length}</td>
                    <td className="xo-num">{pipeline.outputSummary.files}</td><td className="xo-num">{pipeline.outputSummary.importableFiles}</td>
                    <td className="xo-num">{pipeline.outputSummary.columns}</td>
                    <td title={pipeline.references.join('\n')}>{pipeline.references.length}</td>
                    <td className="xo-content-row-action"><span className="xo-row-chevron" aria-hidden="true">
                      <ChevronRight />
                    </span></td>
                  </tr>)}</tbody></table></div>
                </section>

                <section className="xo-release-section"><h3>{tx("ui.releases.deployment_receipts_00332a70")}</h3>
                  {release.attempts.length === 0 ? <p className="xo-muted">{tx("ui.releases.no_durable_receipt_for_this_commit_b1f98f3f")}</p>
                    : <div className="xo-deployment-list">{release.attempts.map((attempt) => <AttemptRow key={attempt.attempt_id}
                      attempt={attempt} onOpen={() => onOpenAttempt(attempt.attempt_id)} />)}</div>}
                </section>
              </div>
            </>
          )}
    </Modal>
  );
}

export default function XomicsReleases() {
  const load = useCallback(() => loadPipelineReleases({ limit: 100 }), []);
  const history = useLiveResource(['pipelines', 'cluster'], load);
  const [query, setQuery] = useState('');
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [view, setView] = useState(() => {
    try { return window.localStorage.getItem('xo-releases-view') === 'list' ? 'list' : 'grid'; }
    catch (_error) { return 'grid'; }
  });
  const data = history.data || { releases: [], attempts: [] };
  const active = data.releases.find((release) => release.status === 'active') || null;
  const latestByCommit = useMemo(() => {
    const map = new Map();
    data.attempts.forEach((attempt) => { if (!map.has(attempt.git_commit)) map.set(attempt.git_commit, attempt); });
    return map;
  }, [data.attempts]);
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return data.releases;
    return data.releases.filter((release) => [release.tag, release.gitCommit, release.notes, release.status]
      .some((value) => value && String(value).toLowerCase().includes(needle)));
  }, [data.releases, query]);

  if (history.loading && history.data === null) return <Loading label={tx("ui.releases.reading_release_history_93c12955")} />;
  if (history.error && history.data === null) return <Failed message={history.error} onRetry={history.refresh} />;
  const failures = data.attempts.filter((attempt) => attempt.status === 'failed').length;
  const latestAttempt = data.attempts[0] || null;

  function changeView(nextView) {
    setView(nextView);
    try { window.localStorage.setItem('xo-releases-view', nextView); } catch (_error) { /* session-only */ }
  }

  return (
    <div className="xo-platform-page">
      <Head title={tx("ui.releases.releases_8fa41d59")} subtitle={active ? tx("ui.releases.active_tag_value_2f0b4f25", { tag: active.tag, value: String(active.gitCommit).slice(0, 12) }) : tx("ui.releases.no_active_pipeline_release_e6cef7c6")} />
      {history.error && <p className="xo-alert t-warn">{history.error}</p>}
      <SummaryBar>
        <SummaryItem value={active ? compactId(active.tag, 18) : '—'} label={tx("ui.releases.active_release_dd19b927")} tone={active ? 'ok' : 'warn'}
          detail={active ? tx("ui.releases.pipelinecount_pipelines_nodecount_targeted_nodes_f177cc46", { pipelineCount: active.pipelineCount, nodeCount: active.nodeCount }) : tx("ui.releases.no_active_manifest_a720d83c")} />
        <SummaryItem value={data.releases.length} label={tx("ui.releases.release_history_cb9b8477")}
          detail={tx("ui.releases.immutable_commit_linked_manifests_859d3f5b")} />
        <SummaryItem value={latestAttempt ? `${latestAttempt.passed_count} / ${latestAttempt.check_count}` : '—'} label={tx("ui.releases.latest_gates_228e1015")}
          tone={latestAttempt?.status === 'active' ? 'ok' : 'warn'} detail={latestAttempt?.summary || tx("ui.releases.no_deployment_receipt_3653f392")} />
        <SummaryItem value={failures} label={tx("ui.releases.failed_attempts_90ced14f")} tone={failures > 0 ? 'warn' : 'ok'}
          detail={failures > 0 ? tx("ui.releases.receipts_retained_below_b31fa803") : tx("ui.releases.no_retained_failures_377b9dfa")} />
      </SummaryBar>

      <div className="xo-release-toolbar">
        <SearchField value={query} onChange={setQuery}
          placeholder={tx("ui.releases.search_tag_commit_or_release_notes_412e0f95")}
          label={tx("ui.releases.search_releases_5ad69f54")} />
        <ViewSwitch value={view} onChange={changeView} label={tx("ui.releases.release_layout_efabbcbb")} />
      </div>

      <section className="xo-release-page-section"><div className="xo-section-heading"><div><h2>{tx("ui.releases.release_history_5044ba0a")}</h2>
        <p>{tx("ui.releases.one_row_per_activated_commit_open_one_for_its_exact_pi_e9291dcb")}</p></div></div>
        <div className={`xo-data-surface xo-release-list is-${view}`}>{shown.length === 0 ? <Empty title={tx("ui.releases.no_releases_match_f22b5509")} />
          : shown.map((release) => <ReleaseRow key={release.id} release={release} attempt={latestByCommit.get(release.gitCommit)}
            onOpen={() => setSelectedRelease(release.id)} />)}</div>
        <p className="xo-list-foot">{tx("ui.releases.showing_length_of_length2_releases_c9b9ec23", { length: shown.length, length2: data.releases.length })}</p>
      </section>

      <section className="xo-release-page-section"><div className="xo-section-heading"><div><h2>{tx("ui.releases.deployment_attempts_3d7096b3")}</h2>
        <p>{tx("ui.releases.durable_ordered_gate_receipts_including_commits_that_w_51a74f40")}</p></div></div>
        <div className={`xo-data-surface xo-deployment-list is-${view}`}>{data.attempts.map((attempt) => <AttemptRow key={attempt.attempt_id} attempt={attempt}
          onOpen={() => setSelectedAttempt(attempt.attempt_id)} />)}</div>
      </section>

      {selectedRelease !== null && <ReleaseDetail id={selectedRelease} onClose={() => setSelectedRelease(null)}
        onOpenAttempt={(id) => { setSelectedRelease(null); setSelectedAttempt(id); }}
        onOpenPipeline={(id) => { setSelectedRelease(null); setSelectedPipeline(id); }} />}
      {selectedAttempt !== null && <DeploymentDetail id={selectedAttempt} onClose={() => setSelectedAttempt(null)} />}
      {selectedPipeline !== null && <PipelineDetail id={selectedPipeline} onClose={() => setSelectedPipeline(null)} />}
    </div>
  );
}
